import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';
import { ClaimsService, blockText } from '../../../services/billing/claims.service';
import { CORRECTION_BY_STATUS } from '../../../dtos/billing/claims.dto';
import type { Claim } from '../../../dtos/billing/claims.dto';

interface ClaimCorrectionDialogProps {
  claim: Claim | null;
  onClose: () => void;
  onCreated: (result: { original: Claim; claim: Claim }) => void;
}

const inputClass = 'w-full rounded-md border border-slate-300 px-3 py-2 text-base focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500';

/**
 * Corrected claim = a new version linked to the original (REQ-BIL-11). Frequency 7 replaces a
 * rejected or denied claim with lines rebuilt from current data; 8 voids a submitted claim at the
 * payer. Box 22 needs the payer claim number (ICN) of the original.
 */
const ClaimCorrectionDialog: React.FC<ClaimCorrectionDialogProps> = ({ claim, onClose, onCreated }) => {
  const id = useId();
  const options = claim ? CORRECTION_BY_STATUS[claim.status] || [] : [];
  const [frequencyCode, setFrequencyCode] = useState<'7' | '8'>('7');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<string[]>([]);

  useEffect(() => {
    if (!claim) return;
    setFrequencyCode(options[0] || '7');
    setReference(claim.payerClaimNumber || '');
    setReason('');
    setError(null);
    setBlocks([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when a different claim opens
  }, [claim?.claimId]);

  if (!claim) return null;

  const submit = async () => {
    setSaving(true);
    setError(null);
    setBlocks([]);
    try {
      onCreated(await ClaimsService.correctClaim(claim.claimId, { frequencyCode, originalReference: reference.trim() || undefined, reason: reason.trim() || undefined }));
    } catch (err) {
      const parsed = ClaimsService.parseGenerationError(err);
      setError(parsed.message);
      if (parsed.kind === 'notReady') setBlocks(parsed.visits.flatMap((v) => v.blocks.map(blockText)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy={`${id}-title`}>
      <h3 id={`${id}-title`} className="text-lg font-semibold text-slate-800">Corrected claim</h3>
      <p className="mt-1 text-sm text-slate-500">Creates version {(claim.version || 1) + 1}. The current claim is kept and marked Corrected.</p>
      <div className="mt-4 space-y-3 text-left">
        <fieldset>
          <legend className="mb-1 text-sm font-medium text-slate-700">Type</legend>
          {options.map((code) => (
            <label key={code} className="flex items-start gap-2 text-sm">
              <input type="radio" name={`${id}-freq`} checked={frequencyCode === code} onChange={() => setFrequencyCode(code)} className="mt-1" />
              <span>
                {code === '7' ? 'Replacement (frequency 7)' : 'Void / cancel (frequency 8)'}
                <span className="block text-xs text-slate-500">
                  {code === '7' ? 'Lines are rebuilt from current visit, coverage and code data.' : 'Same lines; the visits become billable again once it is marked Submitted.'}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
        <div>
          <label htmlFor={`${id}-ref`} className="mb-1 block text-sm font-medium text-slate-700">Original payer claim number (ICN)</label>
          <input id={`${id}-ref`} type="text" maxLength={50} value={reference} onChange={(e) => setReference(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label htmlFor={`${id}-reason`} className="mb-1 block text-sm font-medium text-slate-700">Reason (optional)</label>
          <textarea id={`${id}-reason`} rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} />
        </div>
        {error && (
          <div role="alert" className="text-sm text-red-700">
            <p>{error}</p>
            {blocks.length > 0 && <ul className="mt-1 list-disc pl-5">{[...new Set(blocks)].map((b) => <li key={b}>{b}</li>)}</ul>}
          </div>
        )}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} disabled={saving} className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700">Cancel</button>
        <button type="button" onClick={submit} disabled={saving || !reference.trim()} className="rounded-md bg-slate-800 px-3 py-2 text-sm text-white disabled:bg-slate-400">
          {saving ? 'Creating…' : 'Create corrected claim'}
        </button>
      </div>
    </Dialog>
  );
};

export default ClaimCorrectionDialog;
